/* eslint-disable no-console */
import { useMutation } from '@tanstack/react-query'
import axios from 'axios'
import moment from 'moment'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Environments } from 'src/configurations'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { UserListModel } from 'src/models/user.models'
import {
  VirtualCardSecureModel,
  WalletType,
} from 'src/models/virtualCard.models'
import {
  selectNullSafeLocaleFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectAuthedJsonApiRequestConfig } from 'src/redux/selectors/axios.selectors'
import {
  selectEnvironmentFromState,
  selectGooglePayIntegratorIdFromState,
  selectWalletHostFromState,
} from 'src/redux/selectors/session.selectors'

type GoogleWalletBinaryFlag = 0 | 1

interface GoogleWalletUserAddress {
  name: string
  addressLines: string[]
  localityName: string
  administrativeAreaName: string
  postalCodeNumber: string
  countryCode: string
  phone: string
}

interface GoogleWalletTokenCredentials {
  cardNetwork: string
  tokenServiceProvider: string
  opaquePaymentCard: Uint8Array
}

interface GoogleWalletCardCredentials {
  googleOpaquePaymentCard: Uint8Array
}

interface GoogleWalletPaymentCredentials {
  displayName: string
  lastDigits: string
  userAddress: GoogleWalletUserAddress
  tokenCredentials?: GoogleWalletTokenCredentials
  cardCredentials?: GoogleWalletCardCredentials
}

interface GoogleWalletSessionCreatedPayload {
  clientSessionId?: string
  serverSessionId: string
  tokenSetting: GoogleWalletBinaryFlag
  cardSetting: GoogleWalletBinaryFlag
  publicDeviceId?: string
  publicWalletId?: string
}

interface GoogleWalletulatorWebPushRequestPayload {
  accountNumber: string
  expiryMonth: string
  expiryYear: string
  serverSessionId: string
}

interface GoogleWalletulatorWebPushRequest {
  type: string
  version: string
  time: number
  payload: GoogleWalletulatorWebPushRequestPayload
}

interface GoogleWalletulatorVisaBillingAddress {
  line1: string | null
  line2: string | null
  city: string | null
  state: string | null
  country: string | null
  postalCode: string | null
}

interface GoogleWalletulatorOpcRequestPayload {
  accountNumber: string
  expiryMonth: string
  expiryYear: string
  cardholderName: string | null
  mastercardOpcFormat: 'cardInfo' | 'fundingAccountInfo' | null
  cvv2: string | null
  clientWalletAccountID: string | null
  clientDeviceID: string | null
  billingAddress: GoogleWalletulatorVisaBillingAddress | null
  isIDnV: boolean | null
}

interface GoogleWalletulatorOpcRequest {
  type: string
  version: string
  time: number
  payload: GoogleWalletulatorOpcRequestPayload
}

interface GoogleWalletulatorOpcResponse {
  opc: string | null
  tokenProvider: string | null
  cardNetwork: string | null
}

interface UseGoogleWalletProvisioningProps {
  virtualCard?: VirtualCardSecureModel
  recipient?: UserListModel
  currentUserIsRecipient?: boolean
  walletRecipientIsWhitelisted?: boolean
}

interface UseGoogleWalletProvisioningReturn {
  googleWalletAdded: boolean
  showGoogleAddToWallet: boolean
  onGoogleAddToWalletClick: () => void
}

const GOOGLE_WALLET_TOKEN_SETTING: GoogleWalletBinaryFlag = 1
const GOOGLE_WALLET_CARD_SETTING: GoogleWalletBinaryFlag = 1
const GOOGLE_WALLET_VIRTUAL_CARDS_SETTING: GoogleWalletBinaryFlag = 0
const GOOGLE_WALLET_DEFAULT_CARD_NETWORK = 'CARD_NETWORK_VISA'
const GOOGLE_WALLET_DEFAULT_TOKEN_SERVICE_PROVIDER = 'TOKEN_PROVIDER_VISA'
const GOOGLE_WALLET_PROXY_PATH = '/api/google-wallet'
const GOOGLE_WALLET_DEBUG_ENABLED_FLAG = '__googleWalletDebug'
const GOOGLE_WALLET_DEBUG_ENABLED_STORAGE_KEY = 'google-wallet-debug'
const GOOGLE_WALLET_DEBUG_STORAGE_KEY = 'google-wallet-debug-log'
const GOOGLE_WALLET_DEBUG_LISTENER_FLAG =
  '__googleWalletDebugListenersInstalled'

interface GoogleWalletDebugWindow extends Window {
  [GOOGLE_WALLET_DEBUG_ENABLED_FLAG]?: boolean
  [GOOGLE_WALLET_DEBUG_LISTENER_FLAG]?: boolean
}

export const useGoogleWalletProvisioning = ({
  virtualCard,
  recipient,
  currentUserIsRecipient,
  walletRecipientIsWhitelisted,
}: UseGoogleWalletProvisioningProps): UseGoogleWalletProvisioningReturn => {
  const user = useCurrentUser()
  const locale = useSelector(selectNullSafeLocaleFromState)
  const authedJsonApiRequestConfig = useSelector(selectAuthedJsonApiRequestConfig)
  const googlePayIntegratorId = useSelector(selectGooglePayIntegratorIdFromState)
  const walletHost = useSelector(selectWalletHostFromState)
  const env = useSelector(selectEnvironmentFromState)
  const isProd = env === Environments.Prod

  const googleWalletAdded = false
  const googleAddToWalletEnabled =
    virtualCard?.features.walletsEnabled !== undefined &&
    virtualCard.features.walletsEnabled.includes(WalletType.GooglePay)
  const showGoogleAddToWallet =
    googleAddToWalletEnabled &&
    currentUserIsRecipient === true &&
    walletRecipientIsWhitelisted === true

  const toBase64ByteArray = (value: string): Uint8Array =>
    new TextEncoder().encode(value)

  const isGoogleWalletDebugEnabled = (): boolean => {
    if (typeof window === 'undefined') {
      return false
    }

    const debugWindow = window as GoogleWalletDebugWindow

    if (debugWindow[GOOGLE_WALLET_DEBUG_ENABLED_FLAG] === true) {
      return true
    }

    return (
      window.sessionStorage.getItem(GOOGLE_WALLET_DEBUG_ENABLED_STORAGE_KEY) ===
      '1'
    )
  }

  const sanitizeWalletDebugPayload = (payload: unknown): unknown => {
    if (payload instanceof Uint8Array) {
      return `[Uint8Array length=${payload.length}]`
    }

    if (Array.isArray(payload)) {
      return payload.map((entry) => sanitizeWalletDebugPayload(entry))
    }

    if (payload !== null && typeof payload === 'object') {
      return Object.entries(payload).reduce<Record<string, unknown>>(
        (acc, [key, value]) => {
          if (
            [
              'accountNumber',
              'cvv2',
              'opc',
              'opaquePaymentCard',
              'googleOpaquePaymentCard',
            ].includes(key)
          ) {
            return {
              ...acc,
              [key]:
                key === 'accountNumber' && typeof value === 'string'
                  ? `***${value.slice(-4)}`
                  : '[REDACTED]',
            }
          }

          return {
            ...acc,
            [key]: sanitizeWalletDebugPayload(value),
          }
        },
        {},
      )
    }

    return payload
  }

  const persistWalletBreadcrumb = (
    level: 'log' | 'error',
    message: string,
    payload?: unknown,
  ): void => {
    if (typeof window === 'undefined' || !isGoogleWalletDebugEnabled()) {
      return
    }

    try {
      const rawValue = window.sessionStorage.getItem(
        GOOGLE_WALLET_DEBUG_STORAGE_KEY,
      )
      const existingEntries: Array<Record<string, unknown>> =
        rawValue !== null ? (JSON.parse(rawValue) as Array<Record<string, unknown>>) : []
      const sanitizedPayload = sanitizeWalletDebugPayload(payload)
      const nextEntries = [
        ...existingEntries.slice(-39),
        {
          timestamp: new Date().toISOString(),
          level,
          message,
          payload: sanitizedPayload,
        },
      ]

      window.sessionStorage.setItem(
        GOOGLE_WALLET_DEBUG_STORAGE_KEY,
        JSON.stringify(nextEntries),
      )
    } catch (error) {
      console.error(
        '[Google Wallet] failed to persist breadcrumb',
        sanitizeWalletDebugPayload(error),
      )
    }
  }

  const logWalletEvent = (message: string, payload?: unknown): void => {
    if (!isGoogleWalletDebugEnabled()) {
      return
    }

    const sanitizedPayload = sanitizeWalletDebugPayload(payload)
    persistWalletBreadcrumb('log', message, payload)

    if (sanitizedPayload === undefined) {
      console.log(`[Google Wallet] ${message}`)
      return
    }

    console.log(`[Google Wallet] ${message}`, sanitizedPayload)
  }

  const logWalletError = (message: string, payload?: unknown): void => {
    if (!isGoogleWalletDebugEnabled()) {
      return
    }

    const sanitizedPayload = sanitizeWalletDebugPayload(payload)
    persistWalletBreadcrumb('error', message, payload)

    if (sanitizedPayload === undefined) {
      console.error(`[Google Wallet] ${message}`)
      return
    }

    console.error(`[Google Wallet] ${message}`, sanitizedPayload)
  }

  const installGoogleWalletDebugListeners = (): void => {
    if (typeof window === 'undefined' || !isGoogleWalletDebugEnabled()) {
      return
    }

    const debugWindow = window as GoogleWalletDebugWindow

    if (debugWindow[GOOGLE_WALLET_DEBUG_LISTENER_FLAG] === true) {
      return
    }

    const logLifecycleEvent = (
      message: string,
      payload?: Record<string, unknown>,
    ): void => {
      logWalletEvent(message, payload)
    }

    window.addEventListener('beforeunload', () => {
      logLifecycleEvent('window beforeunload', {
        href: window.location.href,
      })
    })

    window.addEventListener('pagehide', (event) => {
      logLifecycleEvent('window pagehide', {
        href: window.location.href,
        persisted: event.persisted,
      })
    })

    window.addEventListener('pageshow', (event) => {
      logLifecycleEvent('window pageshow', {
        href: window.location.href,
        persisted: event.persisted,
      })
    })

    document.addEventListener('visibilitychange', () => {
      logLifecycleEvent('document visibilitychange', {
        visibilityState: document.visibilityState,
        href: window.location.href,
      })
    })

    debugWindow[GOOGLE_WALLET_DEBUG_LISTENER_FLAG] = true
  }

  useEffect(() => {
    if (typeof window === 'undefined' || !isGoogleWalletDebugEnabled()) {
      return
    }

    installGoogleWalletDebugListeners()

    const rawValue = window.sessionStorage.getItem(
      GOOGLE_WALLET_DEBUG_STORAGE_KEY,
    )

    if (rawValue === null) {
      return
    }

    try {
      const entries = JSON.parse(rawValue) as Array<Record<string, unknown>>

      if (entries.length > 0) {
        console.log(
          '[Google Wallet] restored breadcrumbs from previous page',
          entries,
        )
      }
    } catch (error) {
      console.error(
        '[Google Wallet] failed to restore breadcrumbs',
        sanitizeWalletDebugPayload(error),
      )
    }
  }, [])

  const getCardNetworkForOpcRequest = (
    accountNumber: string,
  ): 'visa' | 'mastercard' => {
    if (accountNumber.startsWith('4')) {
      return 'visa'
    }

    if (
      /^(5[1-5])/.test(accountNumber) ||
      /^(2(2[2-9]|[3-6]|7[01]))/.test(accountNumber)
    ) {
      return 'mastercard'
    }

    return 'visa'
  }

  const getGoogleWalletCardDetails = (): {
    accountNumber: string
    expiryMonth: string
    expiryYear: string
    cvv2: string
  } => {
    if (virtualCard?.vcn === undefined) {
      throw new Error('Google Wallet requires an unredacted virtual card number')
    }

    if (virtualCard.securityCode === undefined) {
      throw new Error('Google Wallet requires an unredacted security code')
    }

    if (virtualCard.expires === undefined) {
      throw new Error('Google Wallet requires a card expiration date')
    }

    return {
      accountNumber: virtualCard.vcn,
      expiryMonth: moment.utc(virtualCard.expires).format('MM'),
      expiryYear: moment.utc(virtualCard.expires).format('YY'),
      cvv2: virtualCard.securityCode,
    }
  }

  const classicOpcMutation = useMutation({
    mutationFn: async ({
      accountNumber,
      expiryMonth,
      expiryYear,
      cardholderName,
      mastercardOpcFormat,
      cvv2,
      clientWalletAccountID,
      clientDeviceID,
      billingAddress,
      isIDnV,
    }: GoogleWalletulatorOpcRequestPayload): Promise<GoogleWalletulatorOpcResponse> => {
      const requestBody: GoogleWalletulatorOpcRequest = {
        type: 'generatePayload',
        version: '1.0.0',
        time: Date.now(),
        payload: {
          accountNumber,
          expiryMonth,
          expiryYear,
          cardholderName,
          mastercardOpcFormat,
          cvv2,
          clientWalletAccountID,
          clientDeviceID,
          billingAddress,
          isIDnV,
        },
      }

      logWalletEvent('classic OPC request', {
        url: GOOGLE_WALLET_PROXY_PATH,
        proxiedWalletHost: walletHost,
        requestBody,
        hasAuthorizationHeader:
          'Authorization' in authedJsonApiRequestConfig.headers,
      })

      const response = await axios.post<GoogleWalletulatorOpcResponse>(
        GOOGLE_WALLET_PROXY_PATH,
        requestBody,
        {
          headers: authedJsonApiRequestConfig.headers,
        },
      )

      logWalletEvent('classic OPC response', response.data)

      return response.data
    },
    mutationKey: ['googleWallet', 'classicOpc'],
  })

  const fetchClassicOpcFromBackend = async (
    _serverSessionId: string,
    publicDeviceId?: string,
    publicWalletId?: string,
  ): Promise<GoogleWalletulatorOpcResponse> => {
    const { accountNumber, expiryMonth, expiryYear, cvv2 } =
      getGoogleWalletCardDetails()
    const card = virtualCard

    if (card === undefined) {
      throw new Error('Google Wallet requires a virtual card')
    }

    const cardNetwork = getCardNetworkForOpcRequest(accountNumber)

    logWalletEvent('using classic OPC card details', {
      accountNumber,
      expiryMonth,
      expiryYear,
      cardNetwork,
      cvv2,
    })

    return classicOpcMutation.mutateAsync({
      accountNumber,
      expiryMonth,
      expiryYear,
      cardholderName: user?.fullName || user?.fullNameOrEmail || null,
      mastercardOpcFormat:
        cardNetwork === 'mastercard' ? 'cardInfo' : null,
      cvv2: cardNetwork === 'visa' ? cvv2 : null,
      clientWalletAccountID:
        cardNetwork === 'visa' ? publicWalletId || null : null,
      clientDeviceID: cardNetwork === 'visa' ? publicDeviceId || null : null,
      billingAddress:
        cardNetwork === 'visa'
          ? {
              line1: card.address?.address1 || null,
              line2:
                card.address?.address2 || card.address?.address1 || null,
              city: card.address?.city || null,
              state: card.address?.province || null,
              country: card.address?.country || null,
              postalCode: card.address?.postal || null,
            }
          : null,
      isIDnV: null,
    })
  }

  const googleOpcMutation = useMutation({
    mutationFn: async ({
      accountNumber,
      expiryMonth,
      expiryYear,
      serverSessionId,
    }: GoogleWalletulatorWebPushRequestPayload): Promise<string> => {
      const requestBody: GoogleWalletulatorWebPushRequest = {
        type: 'generateGoogleWebOpc',
        version: '1.0.0',
        time: Date.now(),
        payload: {
          accountNumber,
          expiryMonth,
          expiryYear,
          serverSessionId,
        },
      }

      logWalletEvent('google OPC request', {
        url: GOOGLE_WALLET_PROXY_PATH,
        proxiedWalletHost: walletHost,
        requestBody,
        hasAuthorizationHeader:
          'Authorization' in authedJsonApiRequestConfig.headers,
      })

      const response = await axios.post<string>(
        GOOGLE_WALLET_PROXY_PATH,
        requestBody,
        {
          headers: authedJsonApiRequestConfig.headers,
        },
      )

      logWalletEvent('google OPC response', response.data)

      return response.data
    },
    mutationKey: ['googleWallet', 'googleOpc'],
  })

  const fetchGoogleOpcBase64BytesFromBackend = async (
    serverSessionId: string,
  ): Promise<Uint8Array> => {
    const { accountNumber, expiryMonth, expiryYear } =
      getGoogleWalletCardDetails()

    const googleOpc = await googleOpcMutation.mutateAsync({
      accountNumber,
      expiryMonth,
      expiryYear,
      serverSessionId,
    })

    return toBase64ByteArray(googleOpc)
  }

  const buildGoogleWalletUserAddress = (): GoogleWalletUserAddress => {
    const line1 = virtualCard?.address?.address1 || ''
    const line2 = virtualCard?.address?.address2 || line1

    return {
      name: user?.fullName || user?.fullNameOrEmail || '',
      addressLines: [line1, line2].filter(Boolean),
      localityName: virtualCard?.address?.city || '',
      administrativeAreaName: virtualCard?.address?.province || '',
      postalCodeNumber: virtualCard?.address?.postal || '',
      countryCode: virtualCard?.address?.country || 'US',
      phone: user?.phone || recipient?.phone || '',
    }
  }

  const createGoogleWalletPaymentCredentials = async (
    sessionPayload: GoogleWalletSessionCreatedPayload,
  ): Promise<GoogleWalletPaymentCredentials> => {
    const [classicOpcResponse, googleOpcBase64Bytes] =
      await Promise.all([
        fetchClassicOpcFromBackend(
          sessionPayload.serverSessionId,
          sessionPayload.publicDeviceId,
          sessionPayload.publicWalletId,
        ),
        fetchGoogleOpcBase64BytesFromBackend(sessionPayload.serverSessionId),
      ])
    const userAddress = buildGoogleWalletUserAddress()

    logWalletEvent('userAddress payload', userAddress)

    if (sessionPayload.tokenSetting === 1 && classicOpcResponse.opc === null) {
      throw new Error('Classic OPC response is missing opc')
    }

    const paymentCredentials = {
      displayName: virtualCard?.displayName || '',
      lastDigits: getGoogleWalletCardDetails().accountNumber.slice(-4),
      userAddress,
      ...(sessionPayload.tokenSetting === 1
        ? {
            tokenCredentials: {
              cardNetwork:
                classicOpcResponse.cardNetwork ||
                GOOGLE_WALLET_DEFAULT_CARD_NETWORK,
              tokenServiceProvider:
                classicOpcResponse.tokenProvider ||
                GOOGLE_WALLET_DEFAULT_TOKEN_SERVICE_PROVIDER,
              opaquePaymentCard: toBase64ByteArray(classicOpcResponse.opc || ''),
            },
          }
        : {}),
      ...(sessionPayload.cardSetting === 1
        ? {
            cardCredentials: {
              googleOpaquePaymentCard: googleOpcBase64Bytes,
            },
          }
        : {}),
    }

    logWalletEvent('paymentCredentials payload', paymentCredentials)

    return paymentCredentials
  }

  const onGoogleAddToWalletClick = (): void => {
    installGoogleWalletDebugListeners()

    logWalletEvent('add to Google Wallet clicked', {
      showGoogleAddToWallet,
      googleAddToWalletEnabled,
      currentUserIsRecipient,
      walletRecipientIsWhitelisted,
      googlePayIntegratorId,
      walletHost,
    })

    if (
      virtualCard === undefined ||
      window.googlepay?.openAppWindow === undefined ||
      window.googlepay?.pushPaymentCredentials === undefined
    ) {
      logWalletEvent('Google Wallet library not ready or virtual card missing')
      return
    }

    window.googlepay.openAppWindow({
      integratorId: googlePayIntegratorId,
      tokenSetting: GOOGLE_WALLET_TOKEN_SETTING,
      cardSetting: GOOGLE_WALLET_CARD_SETTING,
      cardExtraOptions: {
        virtualCardsSetting: GOOGLE_WALLET_VIRTUAL_CARDS_SETTING,
      },
      isTestEnvironment: !isProd,
      clientSessionId: `${virtualCard.id}-${Date.now()}`,
      hl: locale,
      onReady: (): void => {
        logWalletEvent('openAppWindow onReady fired')
      },
      onSessionCreated: async (
        sessionPayload: GoogleWalletSessionCreatedPayload,
      ): Promise<void> => {
        logWalletEvent('session created payload', sessionPayload)

        try {
          const paymentCredentials =
            await createGoogleWalletPaymentCredentials(sessionPayload)

          logWalletEvent('calling pushPaymentCredentials')
          const pushPaymentCredentialsResult =
            window.googlepay?.pushPaymentCredentials?.(paymentCredentials)
          logWalletEvent('pushPaymentCredentials returned', {
            resultType: typeof pushPaymentCredentialsResult,
          })
        } catch (error) {
          logWalletError('session handling failed', error)
          throw error
        }
      },
      onSuccess: (payload: unknown): void => {
        logWalletEvent('onSuccess payload', payload)
      },
      onFailure: (payload: unknown): void => {
        logWalletError('onFailure payload', payload)
      },
      onCancel: (): void => {
        logWalletEvent('onCancel fired')
      },
    })
  }

  return {
    googleWalletAdded,
    showGoogleAddToWallet,
    onGoogleAddToWalletClick,
  }
}
